import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Typography,
  Stack,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  PersonAddAlt1,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetMyDashboardQuery } from '../services/dashboardApi';
import { getEmployees } from '../api/employeeApi';

const Navbar = ({ sidebarOpen, onSidebarToggle }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { isAuthenticated } = useSelector((state) => state.auth);
  const { data: userData } = useGetMyDashboardQuery(undefined, {
    skip: !isAuthenticated,
  });

  const user = userData?.profile || {};
  const userName = `${user.firstName || 'User'} ${user.lastName || ''}`.trim();
  const designation = user.designation?.name || user.role?.name || 'Employee';

  // Debounced search function
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchOptions([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await getEmployees({ search: searchQuery }, { skipLoading: true });
        if (response.success) {
          setSearchOptions(response.data || []);
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearchLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSearchSelect = (event, employee) => {
    if (employee && employee.id) {
      navigate(`/my-team/employee-directory/${employee.id}`);
    }
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary'
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Left Section */}
        <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" sx={{ flexGrow: 1 }}>
          {/* Hamburger Menu */}
          <IconButton
            onClick={onSidebarToggle}
            sx={{ color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Search Input with Autocomplete */}
          <Autocomplete
            open={searchOpen}
            onOpen={() => setSearchOpen(true)}
            onClose={() => setSearchOpen(false)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
            getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''} (${option.employeeId || 'No ID'})`.trim()}
            options={searchOptions}
            loading={searchLoading}
            onInputChange={(event, newInputValue) => {
              setSearchQuery(newInputValue);
            }}
            onChange={handleSearchSelect}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={option.profileImage}
                    sx={{ width: 32, height: 32 }}
                  >
                    {option.firstName?.charAt(0) || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.employeeId} • {option.email}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
            sx={{ width: { xs: 160, sm: 300, md: 400 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by Name, Email, Mobile or ID"
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'grey.50',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'background.paper',
                    },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Stack>

        {/* Right Section */}
        <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
          {/* Notifications */}
          <IconButton sx={{ color: 'text.primary', width: 40, height: 40 }}>
            <Link
              to="/pre-onboarding/initiate"
            >
              <PersonAddAlt1 sx={{ fontSize: 24 }} />
            </Link>
          </IconButton>


          {/* User Profile */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1 }}
            onClick={handleProfileMenuOpen}
          >
            <Avatar
              src={user.profileImage || ""}
              sx={{
                width: 36,
                height: 36,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {!user.profileImage && userName.charAt(0)}
            </Avatar>
            <Stack spacing={0.25} sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}
              >
                {userName}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', lineHeight: 1.2 }}
              >
                {designation}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
